import React, { useState } from "react";
import {
  Card,
  FormLayout,
  TextField,
  OptionList,
  Layout,
  Autocomplete,
  Icon,
  PageActions,
  Text,
  Select,
  Button,
  Collapsible,
  Stack,
  Banner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { SearchMinor, CirclePlusMinor, ChevronDownMinor, ChevronUpMinor } from "@shopify/polaris-icons";
import useLanguageSelector from "../../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../../components/components/LanguageSelector";

export default function TagsForm(props) {
  const { t } = useTranslation();
  
  // Use the new language selector hook
  const {
    currentLocale,
    isLocalizationEnabled,
    localeOptions
  } = useLanguageSelector(props.tag);
  
  const [selectedLocale, setSelectedLocale] = useState(currentLocale || "en");
  const [showTranslations, setShowTranslations] = useState({});

  function updateTag(key, value) {
    props.setTag({ ...props.tag, [key]: value });
  }

  function handleTranslationChange(fieldName, locale, value) {
    let newTag = { ...props.tag };
    
    // Deep clone the translations object to ensure change detection works
    const newTranslations = newTag.translations ? JSON.parse(JSON.stringify(newTag.translations)) : {};
    
    if (!newTranslations[fieldName]) {
      newTranslations[fieldName] = {};
    }
    
    newTranslations[fieldName][locale] = value;
    newTag.translations = newTranslations;
    
    props.setTag(newTag);
  }

  function toggleTranslationVisibility(fieldName) {
    setShowTranslations(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  }

  function getTranslationValue(fieldName, locale) {
    return props.tag?.translations?.[fieldName]?.[locale] || "";
  }

  function getPlaceholderValue(fieldName, locale) {
    if (locale === props.tag?.current_locale) return "";
    return props.tag?.[fieldName] || t("settings.tags.form.defaultValue", { value: props.tag?.[fieldName] || "field value" });
  }

  // Helper function to get locale display name from Rails data
  function getLocaleDisplayName(localeCode) {
    const locale = localeOptions.find(l => l.value === localeCode);
    return locale ? locale.label : localeCode;
  }

  const groupOptions = props.tag.all_groups.map((group) => {
    return {
      value: group,
      label: group,
    };
  });

  const tagGroupTextField = (
    <Autocomplete.TextField
      onChange={(value) => {
        updateTag("group", value);
      }}
      clearButton={true}
      onClearButtonClick={() => updateTag("group", "")}
      label={t("settings.tags.form.group")}
      autoComplete="off"
      labelHidden
      value={props.tag.group || ""}
      prefix={<Icon source={SearchMinor} color="base" />}
      placeholder={t("settings.tags.form.groupPlaceholder")}
      error={formatErrorFor("group")}
    />
  );

  const groupIsNew =
    props.tag.all_groups.filter((group) => {
      return group == props.tag.group;
    }).length == 0 && props.tag.group;

  const newGroupOption = groupIsNew
    ? [
        {
          label: (
            <Text variant="bodyMd" as="span">
              <b>{t("settings.tags.form.addGroup")}</b> {props.tag.group}
            </Text>
          ),
          value: props.tag.group,
          media: <Icon source={CirclePlusMinor} />,
        },
      ]
    : [];

  const tagGroupAutocomplete = (
    <Autocomplete
      textField={tagGroupTextField}
      selected={[props.tag.group]}
      options={newGroupOption.concat(groupOptions)}
      onSelect={(value) => {
        updateTag("group", value[0]);
      }}
    />
  );

  function formatErrorFor(key) {
    if (props.errors && props.errors[key]) {
      const errorMessage = props.errors[key].join(" & ");
      return errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1);
    }
  }

  const isDefaultLocale = selectedLocale === currentLocale;

  return (
    <Layout>
      
      {/* Language Selector Section - Separated */}
      {isLocalizationEnabled && localeOptions.length > 1 && (
        <Layout.Section>
          <Card sectioned>
            <div style={{ marginBottom: '1rem' }}>
              <LanguageSelector
                options={localeOptions}
                value={selectedLocale}
                onChange={setSelectedLocale}
                label={t("settings.tags.form.language")}
                labelHidden={false}
                showIcon={false}
              />
              {!isDefaultLocale && (
                <div style={{ marginTop: '0.5rem' }}>
                  <Text variant="bodyMd" color="subdued">
                    {t("settings.tags.form.editingTranslations", { 
                      language: getLocaleDisplayName(selectedLocale),
                      defaultLanguage: getLocaleDisplayName(currentLocale)
                    })}
                  </Text>
                </div>
              )}
            </div>
          </Card>
        </Layout.Section>
      )}
      
      {/* Form Fields Section - Separated */}
      <Layout.Section>
        <Card sectioned>
          <FormLayout>
            <TextField
              label={t("settings.tags.form.title")}
              value={isDefaultLocale ? (props.tag.title || "") : getTranslationValue('title', selectedLocale)}
              onChange={(value) => {
                if (isDefaultLocale) {
                  updateTag("title", value);
                } else {
                  handleTranslationChange('title', selectedLocale, value);
                }
              }}
              error={formatErrorFor("title")}
              placeholder={!isDefaultLocale ? getPlaceholderValue('title', selectedLocale) : ""}
              helpText={!isDefaultLocale ? t("settings.tags.form.defaultValue", { value: props.tag.title || "tag title" }) : ""}
            />
            <TextField
              label={t("settings.tags.form.description")}
              value={isDefaultLocale ? (props.tag.description || "") : getTranslationValue('description', selectedLocale)}
              multiline={4}
              onChange={(value) => {
                if (isDefaultLocale) {
                  updateTag("description", value);
                } else {
                  handleTranslationChange('description', selectedLocale, value);
                }
              }}
              error={formatErrorFor("description")}
              placeholder={!isDefaultLocale ? getPlaceholderValue('description', selectedLocale) : ""}
              helpText={!isDefaultLocale ? t("settings.tags.form.defaultValue", { value: props.tag.description || "tag description" }) : ""}
            />
          </FormLayout>
        </Card>
        
        <Card sectioned title={t("settings.tags.form.image")}>
          <TextField
            label={t("settings.tags.form.imageUrl")}
            type="url"
            value={props.tag.image_url || ""}
            onChange={(value) => {
              updateTag("image_url", value);
            }}
          />
        </Card>
      </Layout.Section>
      
      <Layout.Section secondary>
        <Card title="Display on">
          <OptionList
            onChange={(selectedValues) => {
              updateTag("show_on_filter", selectedValues.includes("filters"));
            }}
            options={[{ value: "filters", label: "Filters" }]}
            selected={props.tag.show_on_filter == true ? ["filters"] : []}
            allowMultiple
          />
        </Card>
        
        <Card title="Tag group" sectioned>
          {isDefaultLocale ? (
            tagGroupAutocomplete
          ) : (
            <TextField
              label={t("settings.tags.form.group")}
              value={getTranslationValue('group', selectedLocale)}
              onChange={(value) => handleTranslationChange('group', selectedLocale, value)}
              placeholder={getPlaceholderValue('group', selectedLocale)}
              helpText={t("settings.tags.form.defaultValue", { value: props.tag.group || "tag group" })}
              error={formatErrorFor("group")}
            />
          )}
        </Card>
      </Layout.Section>
      
      <Layout.Section>
        {(props.primaryAction || props.secondaryActions) && (
          <PageActions
            primaryAction={props.primaryAction}
            secondaryActions={props.secondaryActions}
          />
        )}
      </Layout.Section>
    </Layout>
  );
}
